import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import type { LicenseType } from '@ebalance/proto/bfftypes/v1/license_type_pb'

import type { PositionChartSlot } from '~/components/bidding/common/types'
import { bidIssuerTypeOf } from '~/models/bidIssuerType'
import type { BlockBidRow } from '~/pages/SpotBidding/models/blockBidRow'
import { buildBlockPositionSlots } from '~/pages/SpotBidding/models/convertBlockBidsFromApprovalRequests'
import { isInPageIssuerScope } from '~/pages/SpotBidding/models/pageIssuerScope'
import type { SpotBidRow } from '~/pages/SpotBidding/models/spotBidRow'
import {
  buildPositionSlots,
  combineSpotPositionSlots,
} from '~/pages/SpotBidding/models/spotBidValidation'

/**
 * 共通ツールバーで選んだ発注主体区分の需給バランスに載る通常札。 調達必要量と突き合わせる札
 * (入札時カバー量、 札作成の数量の既定値) は同じ区分の札に限る (AC-BSP-102 ※)。 区分の判定は
 * 入札一覧と同じ [[isInPageIssuerScope]] で、 区分不明の札は別の項目で数える (AC-BSP-127) ので残す。
 */
export const normalBidsOfIssuerPosition = <
  T extends Pick<SpotBidRow, 'issuerType'>,
>(
  bids: readonly T[],
  issuer: LicenseType,
): T[] => bids.filter((b) => isInPageIssuerScope(b.issuerType, issuer))

/** [[normalBidsOfIssuerPosition]] のブロック入札の行。 */
export const blockRowsOfIssuerPosition = <
  T extends Pick<BlockBidRow, 'issuerType'>,
>(
  rows: readonly T[],
  issuer: LicenseType,
): T[] =>
  rows.filter((r) =>
    isInPageIssuerScope(
      r.issuerType === undefined ? undefined : bidIssuerTypeOf(r.issuerType),
      issuer,
    ),
  )

/**
 * 需給バランスチャートの 48 コマ: チャート対象エリアの、 選んだ区分の通常札とブロック行を
 * 合算する (AC-BSP-016 / AC-BSP-102 ※)。 締切通過は札の状態区分の代理判定に使う
 * (AC-BSP-108、 `deriveSpotBidStatusCategory`)。
 */
export const buildIssuerPositionSlots = ({
  bids,
  blockRows,
  area,
  issuer,
  needByKoma,
  isPastDeadline,
}: {
  bids: readonly SpotBidRow[]
  blockRows: readonly BlockBidRow[]
  area: AreaCode
  issuer: LicenseType
  needByKoma: readonly number[] | undefined
  isPastDeadline: boolean
}): PositionChartSlot[] =>
  combineSpotPositionSlots(
    buildPositionSlots(
      normalBidsOfIssuerPosition(
        bids.filter((b) => b.areaCode === area),
        issuer,
      ),
      needByKoma,
      isPastDeadline,
    ),
    buildBlockPositionSlots(
      blockRowsOfIssuerPosition(
        blockRows.filter((r) => r.areaCode === area),
        issuer,
      ),
      needByKoma,
      isPastDeadline,
    ),
  )
